import type { WordPack } from "./types";
import { WORD_PACK_DEFINITIONS } from "./word-packs";

const ALL_PACKS: WordPack[] = Object.keys(WORD_PACK_DEFINITIONS) as WordPack[];

export interface SemanticResolver {
  getFamilyIds: (word: string) => number[];
  shares: (wordA: string, wordB: string) => boolean;
  hasTriple: (words: string[]) => boolean;
  countRelated: (anchorWord: string, words: string[]) => number;
}

const resolverCache = new Map<string, SemanticResolver>();
const MAX_RESOLVER_CACHE_SIZE = 64;

function normalizeWords(words: string[]): string[] {
  return Array.from(
    new Set(
      words
        .map((word) => word.trim().toUpperCase())
        .filter(Boolean)
    )
  ).sort();
}

function getFamilyKey(words: string[]): string {
  return Array.from(new Set(words.map((word) => word.toUpperCase()))).sort().join("|");
}

function buildResolver(activePacks: WordPack[], semanticWords: string[]): SemanticResolver {
  const semanticWordSet = new Set(semanticWords);
  const allowedWords = new Set<string>();
  for (const pack of activePacks) {
    for (const word of WORD_PACK_DEFINITIONS[pack]?.words ?? []) {
      allowedWords.add(word.toUpperCase());
    }
  }
  for (const word of semanticWordSet) {
    allowedWords.add(word);
  }

  const candidateFamilies: string[][] = [];
  const seenFamilyKeys = new Set<string>();

  const addFamily = (family: string[]) => {
    const normalizedFamily = normalizeWords(family);
    if (normalizedFamily.length < 2) return;

    const familyKey = getFamilyKey(normalizedFamily);
    if (seenFamilyKeys.has(familyKey)) return;
    seenFamilyKeys.add(familyKey);
    candidateFamilies.push(normalizedFamily);
  };

  for (const pack of activePacks) {
    for (const family of WORD_PACK_DEFINITIONS[pack]?.semanticFamilies ?? []) {
      addFamily(family);
    }
  }

  // Custom semantic words may reference words from packs outside the selected set.
  // Pulling matching families lets on-board custom words participate in scoring.
  if (semanticWordSet.size > 0) {
    for (const pack of ALL_PACKS) {
      for (const family of WORD_PACK_DEFINITIONS[pack]?.semanticFamilies ?? []) {
        if (!family.some((word) => semanticWordSet.has(word.toUpperCase()))) continue;
        addFamily(family);
      }
    }
  }

  const wordToFamilyIds = new Map<string, number[]>();
  for (const [familyId, familyWords] of candidateFamilies.entries()) {
    const filtered = familyWords.filter((word) => allowedWords.has(word));
    if (filtered.length < 2) continue;
    for (const word of filtered) {
      const ids = wordToFamilyIds.get(word);
      if (ids) ids.push(familyId);
      else wordToFamilyIds.set(word, [familyId]);
    }
  }

  const getFamilyIds = (word: string): number[] => wordToFamilyIds.get(word.toUpperCase()) ?? [];
  const shares = (wordA: string, wordB: string): boolean => {
    const aFamilies = getFamilyIds(wordA);
    if (aFamilies.length === 0) return false;
    const bFamilies = new Set(getFamilyIds(wordB));
    if (bFamilies.size === 0) return false;
    for (const familyId of aFamilies) if (bFamilies.has(familyId)) return true;
    return false;
  };
  const hasTriple = (words: string[]): boolean => {
    if (words.length < 3) return false;
    const counts = new Map<number, number>();
    for (const word of words) {
      for (const familyId of getFamilyIds(word)) {
        counts.set(familyId, (counts.get(familyId) ?? 0) + 1);
      }
    }
    for (const count of counts.values()) if (count >= 3) return true;
    return false;
  };
  const countRelated = (anchorWord: string, words: string[]): number =>
    words.reduce((total, word) => total + (shares(anchorWord, word) ? 1 : 0), 0);

  return { getFamilyIds, shares, hasTriple, countRelated };
}

export function getSemanticResolver(
  activePacks?: WordPack[],
  semanticWords: string[] = []
): SemanticResolver {
  const normalizedPacks = !activePacks || activePacks.length === 0
    ? ALL_PACKS
    : Array.from(new Set(activePacks));
  const normalizedSemanticWords = normalizeWords(semanticWords);
  const key = `${normalizedPacks.slice().sort().join("|")}::${normalizedSemanticWords.join("|")}`;
  const cached = resolverCache.get(key);
  if (cached) return cached;

  if (resolverCache.size >= MAX_RESOLVER_CACHE_SIZE) {
    resolverCache.clear();
  }

  const resolver = buildResolver(normalizedPacks, normalizedSemanticWords);
  resolverCache.set(key, resolver);
  return resolver;
}
